import { toast } from "react-toastify";
import { buildLoginCredentialsMessage } from "../../helpers/loginCredentials";
import React from "react";
import { AlertCircleIcon } from "lucide-react";
import {
  FaCrown,
  FaDownload,
  FaEdit,
  FaEye,
  FaHeart,
  FaLock,
  FaPaperPlane,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import {
  getImagePrivacyDisplayLabel,
  normalizePrivacyValue,
} from "../../utils/imagePrivacy";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { hasActiveMembership } from "../../utils/bureau";

type Props = {
  profile: any;
  handleToggleActiveStatus: () => void;
  openPrivacyModal: () => void;
  openDeleteModal: () => void;
  openSuspendModal: () => void;
  openPasswordModal: (password: string) => void;
  setIsDownloadModalOpen: (open: boolean) => void;
  openServicePreferenceModal: () => void;
  imagePrivacyOptions: any;
  setIsImagePrivacyModalOpen: (open: boolean) => void;
};

const ProfileActions = ({
  profile,
  handleToggleActiveStatus,
  openPrivacyModal,
  openDeleteModal,
  openSuspendModal,
  openPasswordModal,
  setIsDownloadModalOpen,
  openServicePreferenceModal,
  imagePrivacyOptions,
  setIsImagePrivacyModalOpen,
}: Props) => {
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);

  const isPaidAccount = hasActiveMembership(bureauInfo?.paymentStatus);

  // Send credentials via WhatsApp
  const sendCredentials = (password: string, maritalId: string, fullName: string) => {
    try {
      const message = buildLoginCredentialsMessage({
        fullName,
        martialId: maritalId,
        password,
        websiteUrl: bureauInfo?.websiteUrl,
        websiteLinks: bureauInfo?.websiteLinks,
      });
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to prepare login credentials.");
    }
  };
  const navigate = useNavigate();
  const getImagePrivacyLabel = (value: any) => {
    const normalizedValue = normalizePrivacyValue(value);
    if (!normalizedValue) {
      return "Not Set";
    }

    const found = imagePrivacyOptions.find(
      (opt: any) => normalizePrivacyValue(opt.value) === normalizedValue
    );
    if (found) return found.label;

    const derived = getImagePrivacyDisplayLabel(value);
    if (derived) return derived;

    return value ? value : "Not Set";
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Active Status Toggle (now first) */}
      <button
        onClick={handleToggleActiveStatus}
        className={`bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center ${
          profile.activeStatus ? "border-green-500" : "border-red-500"
        }`}
      >
        <FaUser
          className={
            profile.activeStatus ? "text-green-600 mr-3" : "text-red-600 mr-3"
          }
          size={20}
        />
        <div className="text-left">
          <p className="font-semibold">
            {profile.activeStatus ? "Deactivate Profile" : "Activate Profile"}
          </p>
          <p
            className={`text-xs ${
              profile.activeStatus ? "text-green-600" : "text-red-600"
            }`}
          >
            {profile.activeStatus ? "Active" : "Inactive"}
          </p>
        </div>
      </button>

      <button
        // onClick={openPrivacyModal}
        onClick={(event) => {
          if (!isPaidAccount) {
            event.preventDefault();
            navigate("/admin/membership/plans", { state: { fromShare: true } });
          } else {
            openPrivacyModal();
          }
        }}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaLock className="text-purple-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Profile Privacy</p>
          <p className="text-sm text-gray-600">Manage visibility settings</p>
        </div>
      </button>

      {/* Report Profile */}
      {/* {isPaidAccount && ( */}
      <button
        // onClick={openDeleteModal}
        onClick={(event) => {
          if (!isPaidAccount) {
            event.preventDefault();
            navigate("/admin/membership/plans", { state: { fromShare: true } });
          } else {
            openDeleteModal();
          }
        }}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaTrash className="text-red-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Delete Profile</p>
          <p className="text-sm text-gray-600">Delete issues</p>
        </div>
      </button>
      {/* )} */}

      <button
        onClick={openSuspendModal}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <AlertCircleIcon className="text-red-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Suspend Profile</p>
        </div>
      </button>

      {/* Send Credentials */}
      <button
        onClick={() =>
          sendCredentials(profile.password, profile.martialId, profile.fullName)
        }
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaPaperPlane className="text-green-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Share login details</p>
        </div>
      </button>

      {/* View Password */}
      <button
        onClick={() => openPasswordModal(profile.password)}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaEye className="text-orange-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">View Password</p>
          <p className="text-sm text-gray-600">Show login password</p>
        </div>
      </button>

      {/* View Suitable Profiles */}
      <button
        onClick={() =>
          navigate(
            `/admin/profile/suitable/${encodeURIComponent(profile.martialId)}`,
          )
        }
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaHeart className="text-pink-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">View Suitable Profiles</p>
          <p className="text-sm text-gray-600">Matches for partner preferences</p>
        </div>
      </button>

      {/* Edit Profile */}
      <a
        href={`/admin/edit-profile/${profile.martialId}`}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaEdit className="text-blue-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Edit Profile</p>
          <p className="text-sm text-gray-600">Modify profile details</p>
        </div>
      </a>

      {/* Download Biodata */}
      <button
        onClick={() => setIsDownloadModalOpen(true)}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center disabled:opacity-50"
      >
        <FaDownload className="text-purple-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Download Biodata</p>
          <p className="text-sm text-gray-600">Download as PDF or Image</p>
        </div>
      </button>

      {/* Payment Status Toggle */}
      <button
        onClick={openServicePreferenceModal}
        className="bg-white p-4 rounded-lg shadow flex items-center"
      >
        <FaCrown className="text-yellow-600 mr-3" />
        <div className="text-left">
          <p className="font-semibold">
            {profile.paymentStatus === "paid"
              ? "Manage Service Preference"
              : "Upgrade to Paid Profile"}
          </p>
          <p className="text-xs text-gray-600">
            {profile.paymentStatus === "paid"
              ? "Change service"
              : "Select service & pay"}
          </p>
        </div>
      </button>

      {/* Image Privacy */}
      <button
        onClick={(event) => {
          if (!isPaidAccount) {
            event.preventDefault();
            navigate("/admin/membership/plans", { state: { fromShare: true } });
          } else {
            setIsImagePrivacyModalOpen(true);
          }
        }}
        className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow flex items-center"
      >
        <FaLock className="text-purple-600 mr-3" size={20} />
        <div className="text-left">
          <p className="font-semibold">Image Privacy</p>
          <p className="text-sm text-gray-600">
            {getImagePrivacyLabel(profile.imagePrivacy)}
          </p>
        </div>
      </button>
    </div>
  );
};

export default ProfileActions;
